import React, { useMemo, useState } from 'react';
import Select, { SelectOption } from '@amzn/meridian/select';
import { AssignmentAssociate } from '../models';
import { filterUnassignedAssociates } from '../utils';
import { getAssociateDisplayLabel } from '../../utils';

export interface AddAssociateSlotProps {
  /** The unassigned associate pool offered in the typeahead. */
  unassignedAssociates: AssignmentAssociate[];
  /**
   * Commits the pick with the chosen associate's id. The parent binds this to
   * the concrete target (a workstation slot or a non-workstation slot), so the
   * control stays agnostic to where the associate lands.
   */
  onSelect: (associateId: string) => void;
  /** The Select's data-testid, supplied by the parent per slot. */
  dataTestId: string;
}

/**
 * Inline "Add associate" control shown on an empty (planned-but-unfilled)
 * slot — used by line lanes ({@link WorkstationCard}) and the no-lane pool
 * body ({@link ProcessPathSection}), for both workstation and
 * non-workstation slots. A Meridian `Select` in custom-search mode
 * (`searchQuery`/`onSearch`) renders a search box plus a filtered list of
 * unassigned associates — an all-Meridian typeahead. Picking an option fires
 * `onSelect`; the slot then re-renders as occupied, so the Select's own
 * `value` never needs to persist a selection.
 *
 * Options are labelled by login alias when present, falling back to the
 * display name (see `getAssociateDisplayLabel`).
 *
 * Holds only its own transient search text; the associate pool and the commit
 * handler are owned by the parent.
 */
const AddAssociateSlot: React.FC<AddAssociateSlotProps> = ({
  unassignedAssociates,
  onSelect,
  dataTestId,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('');

  const options = useMemo(
    () => filterUnassignedAssociates(unassignedAssociates, searchQuery),
    [unassignedAssociates, searchQuery],
  );

  return (
    <Select
      value={undefined}
      onChange={(associateId: string) => {
        setSearchQuery('');
        onSelect(associateId);
      }}
      searchQuery={searchQuery}
      onSearch={setSearchQuery}
      placeholder="Add associate"
      size="small"
      data-testid={dataTestId}
    >
      {options.map((associate) => (
        <SelectOption
          key={associate.associateId}
          value={associate.associateId as string}
          label={getAssociateDisplayLabel(associate.associateAlias, associate.associateName)}
        />
      ))}
    </Select>
  );
};

export default AddAssociateSlot;
